import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { BellIcon } from './BellIcon';
import type { NotificationFeed } from '../lib/types';
import { shortAgo } from '../lib/types';

/**
 * The floor, not the mechanism.
 *
 * Notifications arrive over an open line now (see the effect below), so this exists for when
 * that line cannot be had: a proxy that buffers it, a second replica that wrote the row, a
 * network that dropped it between reconnections. Left at a minute because that is all it has
 * to be once something faster is doing the work.
 */
const POLL_MS = 60_000;

/**
 * Notifications were being written and never shown — a comment or an @mention reached nobody.
 * This is where they surface: an unread count on the bell, and a panel that takes you to the
 * thing being talked about.
 */
export function NotificationBell() {
  const [feed, setFeed] = useState<NotificationFeed | null>(null);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  /** The panel itself, which lives in the body rather than under the wrapper. */
  const panel = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  /**
   * The panel is fixed-position, so it needs the button's place on screen. Measured on open
   * and kept inside the viewport, since the bell sits in a narrow column near the top-left.
   */
  function place() {
    const rect = button.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 320;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    setAnchor({ top: rect.bottom + 8, left });
  }

  const loadCount = useCallback(async () => {
    try {
      const result = await api.get<{ unread: number }>('/notifications/count');
      setUnread(result.unread);
    } catch {
      /* the bell is not worth interrupting anyone over */
    }
  }, []);

  const loadFeed = useCallback(async () => {
    try {
      const result = await api.get<NotificationFeed>('/notifications?limit=20');
      setFeed(result);
      setUnread(result.unread);
    } catch {
      setFeed({ unread: 0, items: [] });
    }
  }, []);

  // The count is cheap, so poll that rather than the whole list.
  useEffect(() => {
    void loadCount();
    const id = window.setInterval(loadCount, POLL_MS);
    return () => window.clearInterval(id);
  }, [loadCount]);

  /**
   * The open line, so a notification shows up when it happens rather than up to a minute
   * later — which is what made people refresh the page to check.
   *
   * WHAT ARRIVES IS A NUDGE, AND THE COUNT IS RE-ASKED. The message carries no number, so a
   * duplicate, one that arrives out of order, or a burst on reconnection all end in the same
   * place: one cheap request for the truth. A count pushed down the wire would eventually be
   * wrong and there would be no way to tell.
   *
   * `EventSource` reconnects by itself, so an error handler that tears it down would make
   * things worse — a dropped line recovers on its own, and the poll above covers the gap.
   * The re-ask on RECONNECTION is the important one: anything that landed while the line was
   * down is caught the moment it comes back.
   */
  useEffect(() => {
    if (typeof EventSource === 'undefined') return;

    const source = new EventSource('/api/notifications/stream', { withCredentials: true });
    source.onopen = () => void loadCount();
    source.onmessage = (event) => {
      // The heartbeat keeps proxies from closing the line; it is not news.
      try {
        if (JSON.parse(event.data)?.type === 'ping') return;
      } catch {
        /* Unparseable is still a sign of life — ask for the count and move on. */
      }
      void loadCount();
    };

    return () => source.close();
  }, [loadCount]);

  /**
   * Coming back to the tab is its own reason to check. A browser throttles timers in a hidden
   * tab and may hold the stream, so the count on screen when somebody returns to it is the one
   * from whenever it was last awake.
   */
  useEffect(() => {
    /*
      Two different events, and only one of them needs the guard. `visibilitychange` fires in
      both directions, so it has to ask which way it went — refreshing on the way OUT would be
      a request nobody is there to see. `focus` only ever means the window came forward, so
      asking again there is redundant, and wrong wherever the two disagree: a window can take
      focus while the tab still reports itself hidden, and this then refreshed nothing.
    */
    const onShown = () => {
      if (document.visibilityState === 'visible') void loadCount();
    };
    const onFocus = () => void loadCount();

    document.addEventListener('visibilitychange', onShown);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onShown);
      window.removeEventListener('focus', onFocus);
    };
  }, [loadCount]);

  useEffect(() => {
    if (open) void loadFeed();
  }, [open, loadFeed]);

  // Clicking anywhere else, or pressing Escape, closes it.
  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node;
      /**
       * The panel is rendered into the body, so it is NOT inside the wrapper any more and has
       * to be asked about separately — checking only the wrapper closed the panel on the way
       * to clicking anything in it.
       */
      const inButton = wrapper.current?.contains(target) ?? false;
      const inPanel = panel.current?.contains(target) ?? false;
      if (!inButton && !inPanel) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    const reposition = () => place();
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  async function openTarget(
    id: string,
    target: NotificationFeed['items'][number]['target'],
    entityType?: string | null,
  ) {
    setOpen(false);
    await api.post('/notifications/read', { ids: [id] }).catch(() => {});
    await loadCount();
    // Straight to the task, opened in the panel on the right — being told about something and
    // then having to hunt for it is most of the annoyance of a notification.
    /* An invitation has no task behind it: it is answered on its own page. */
    if (entityType === 'company_invitation') {
      navigate('/invitations');
      return;
    }
    if (target) navigate(`/projects/${target.projectId}/board?task=${target.id}`);
  }

  async function dismiss(id: string, event: React.MouseEvent) {
    // Clearing one must not also open it.
    event.stopPropagation();
    setBusy(true);
    try {
      await api.del(`/notifications/${id}`);
      await loadFeed();
    } finally {
      setBusy(false);
    }
  }

  async function clearRead() {
    setBusy(true);
    try {
      const result = await api.del<{ cleared: number }>('/notifications');
      await loadFeed();
      if (result.cleared === 0) setHint('Nothing read yet to clear.');
    } finally {
      setBusy(false);
    }
  }

  async function markAll() {
    setBusy(true);
    try {
      await api.post('/notifications/read-all');
      await loadFeed();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={wrapper} style={{ position: 'relative' }}>
      <button
        ref={button}
        type="button"
        className={unread > 0 ? 'bell has-unread' : 'bell'}
        aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
        aria-expanded={open}
        onClick={() => {
          if (!open) place();
          setOpen(!open);
        }}
      >
        <BellIcon />
        {unread > 0 && <span className="bell-count">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open &&
        anchor &&
        createPortal(
          <div
            ref={panel}
            className="bell-panel"
            role="dialog"
            aria-label="Notifications"
            style={{ top: anchor.top, left: anchor.left }}
          >
            <div className="row-between" style={{ padding: '10px 13px', borderBottom: '1px solid var(--line)' }}>
              <strong style={{ fontSize: 13 }}>Notifications</strong>
              <span className="row" style={{ gap: 2 }}>
                {unread > 0 && (
                  <button type="button" className="btn btn-quiet btn-sm" disabled={busy} onClick={markAll}>
                    Mark all read
                  </button>
                )}
                {feed && feed.items.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    disabled={busy}
                    onClick={clearRead}
                    title="Removes the ones already read; anything unread stays"
                  >
                    Clear read
                  </button>
                )}
              </span>
            </div>

            {hint && (
              <div className="dim" style={{ padding: '8px 13px', fontSize: 12, borderBottom: '1px solid var(--line)' }}>
                {hint}
              </div>
            )}

            {feed === null ? (
              <div className="dim" style={{ padding: 16, fontSize: 13 }}>
                Loading…
              </div>
            ) : feed.items.length === 0 ? (
              <div className="dim" style={{ padding: 16, fontSize: 13 }}>
                Nothing yet. You will hear about work assigned to you, and about comments on
                anything you raised or were mentioned in.
              </div>
            ) : (
              feed.items.map((item) => (
                <div
                  key={item.id}
                  className={item.readAt ? 'bell-item' : 'bell-item unread'}
                  role="button"
                  tabIndex={0}
                  onClick={() => openTarget(item.id, item.target, item.entityType)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      void openTarget(item.id, item.target, item.entityType);
                    }
                  }}
                >
                  <div className="row-between" style={{ gap: 8, alignItems: 'flex-start' }}>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, lineHeight: 1.45 }}>{item.message}</div>
                      <div className="row" style={{ gap: 7, marginTop: 3 }}>
                        {item.target && <span className="key">{item.target.key}</span>}
                        <span className="dim" style={{ fontSize: 11 }}>
                          {shortAgo(item.createdAt)}
                        </span>
                        {!item.target && item.entityId && (
                          <span className="dim" style={{ fontSize: 11 }}>
                            · the item is gone
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="bell-dismiss"
                      aria-label="Clear this notification"
                      title="Clear this notification"
                      disabled={busy}
                      onClick={(event) => dismiss(item.id, event)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>,
          // Into the body, not the sidebar. The sidebar is position:sticky, which creates a
          // stacking context, so no z-index on the panel could lift it above the page's own
          // content — the board's columns were painting over the top of it.
          document.body,
        )}
    </div>
  );
}
